<template>
  <div class="materijal-preview">
    <template v-if="!loading && hasData">
      <!-- Header: transparent bar like main site (logo left, nav right) -->
      <header class="materijal-preview__site-header">
        <nav class="materijal-preview__ww materijal-preview__nav-wrap">
          <span class="materijal-preview__logo">M.MONDIAL90.</span>
          <ul class="materijal-preview__nav">
            <li><span>O Nama</span></li>
            <li><span>Projekti</span></li>
            <li><span>Galerija</span></li>
            <li class="materijal-preview__nav-dd"><span>Materijali</span> <i class="materijal-preview__caret" /></li>
            <li class="materijal-preview__nav-dd"><span>Proizvodi</span> <i class="materijal-preview__caret" /></li>
            <li><span>Partneri</span></li>
            <li><span>Kontakt</span></li>
          </ul>
        </nav>
      </header>

      <!-- Hero: matches main site Cover (title + language selector + border) -->
      <div
        class="materijal-preview__cover"
        :style="{ backgroundImage: hasHero ? `url(${coverImageUrl})` : 'none' }">
        <div class="materijal-preview__cover-ww">
          <h1 class="materijal-preview__cover-title">{{ material.displayName || 'Material' }}</h1>
          <span class="materijal-preview__lang">SRB / ENG</span>
        </div>
        <div class="materijal-preview__cover-border" />
        <div class="materijal-preview__cover-overlay" />
      </div>

      <!-- Main content: matches main site material page -->
      <div class="materijal-preview__main ww">
        <template v-for="sectionId in previewSectionOrder">
          <section v-if="sectionId === 'general' && material.narrative" :key="sectionId" class="materijal-preview__section info-text">
            <div class="main-text" v-html="material.narrative" />
          </section>
          <section v-else-if="sectionId === 'youtube' && videoEmbedUrl" :key="sectionId" class="materijal-preview__section">
            <h2 class="materijal-preview__section-title">Video</h2>
            <div class="materijal-preview__video-wrap">
              <iframe
                :src="videoEmbedUrl"
                class="materijal-preview__video"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              />
            </div>
          </section>
          <section v-else-if="sectionId === 'galerija' && material.gallery && material.gallery.length" :key="sectionId" class="materijal-preview__section">
            <h2 class="materijal-preview__section-title">Galerija</h2>
            <div class="materijal-preview__gallery">
              <img
                v-for="(img, i) in material.gallery"
                :key="i"
                :src="img.url"
                class="materijal-preview__gallery-img"
                alt=""
              >
            </div>
          </section>
        </template>
      </div>

      <!-- Footer: matches main site MainFooter layout and visual -->
      <footer class="materijal-preview__footer">
        <div class="materijal-preview__footer-inner">
          <div class="materijal-preview__footer-desc">
            <h2>Mondial 90 d.o.o.</h2>
            <p>Uvoz i izrada kvarcnih, mermernih i granitnih ploča</p>
          </div>
          <ul class="materijal-preview__footer-right">
            <li class="materijal-preview__footer-products">
              <span>Proizvodi</span>
              <p>Radne ploče</p>
              <p>Kvarcne radne ploče</p>
              <p>Mermerne ploče za kupatilo i kuhinje</p>
            </li>
            <li>
              <span>Adresa</span>
              <p>Vodovodska 158,<br>Žarkovo, 11090<br>Beograd</p>
            </li>
            <li>
              <span>Kontakt</span>
              <p>+381 66 80 36 149<br><br>office@mondial90.rs</p>
            </li>
          </ul>
        </div>
        <div class="materijal-preview__footer-copy">© {{ new Date().getFullYear() }}. Mondial 90 by <a href="http://www.izrada-web-sajtova.rs" target="_blank" rel="noopener">TimePad</a></div>
      </footer>
    </template>

    <p v-if="!hasData && !loading" class="materijal-preview__empty">No preview data. Use Preview from the material edit page.</p>
    <p v-if="loading" class="materijal-preview__loading">Loading preview…</p>
  </div>
</template>

<script>
const STORAGE_KEY = 'mondialMaterialPreview'

function getVideoEmbedUrl (url) {
  if (!url || typeof url !== 'string') return ''
  const trimmed = url.trim()
  const ytMatch = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`
  const vimeoMatch = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`
  if (trimmed.includes('youtube.com/embed/') || trimmed.includes('player.vimeo.com/')) return trimmed
  return ''
}

export default {
  name: 'MaterijalPreview',

  data () {
    return {
      loading: true,
      material: {
        displayName: '',
        narrative: '',
        videoEmbedUrl: '',
        gallery: [],
        squareTile: null,
        heroBanner: null
      }
    }
  },

  computed: {
    hasData () {
      const m = this.material
      return m.displayName || m.narrative || (m.gallery && m.gallery.length) || m.squareTile || m.heroBanner
    },
    hasHero () {
      return !!(this.material.heroBanner || this.material.squareTile)
    },
    coverImageUrl () {
      return this.material.heroBanner || this.material.squareTile || ''
    },
    videoEmbedUrl () {
      return getVideoEmbedUrl(this.material.videoEmbedUrl)
    },
    previewSectionOrder () {
      const order = this.material.sectionOrder
      if (Array.isArray(order) && order.length === 3) return order
      return ['general', 'galerija', 'youtube']
    }
  },

  created () {
    this.loadFromStorage()
  },

  methods: {
    loadFromStorage () {
      const maxWait = 15000
      const start = Date.now()
      const poll = () => {
        try {
          const raw = localStorage.getItem(STORAGE_KEY)
          if (raw) {
            const data = JSON.parse(raw)
            if (data._loading === true) {
              if (Date.now() - start < maxWait) {
                this.pollTimer = setTimeout(poll, 100)
              } else {
                this.loading = false
              }
              return
            }
            this.material = {
              displayName: data.displayName || '',
              narrative: data.narrative || '',
              videoEmbedUrl: data.videoEmbedUrl || '',
              gallery: Array.isArray(data.gallery) ? data.gallery : [],
              squareTile: data.squareTile || null,
              heroBanner: data.heroBanner || null,
              sectionOrder: Array.isArray(data.sectionOrder) && data.sectionOrder.length === 3
                ? data.sectionOrder
                : ['general', 'galerija', 'youtube']
            }
            localStorage.removeItem(STORAGE_KEY)
            this.loading = false
            return
          }
        } catch (e) {
          this.loading = false
          return
        }
        if (Date.now() - start < maxWait) {
          this.pollTimer = setTimeout(poll, 150)
        } else {
          this.loading = false
        }
      }
      poll()
    }
  },

  beforeDestroy () {
    if (this.pollTimer) clearTimeout(this.pollTimer)
  }
}
</script>

<style lang="scss" scoped>
.materijal-preview {
  min-height: 100vh;
  font-family: 'Open Sans', sans-serif;
  background: #fff;
  font-size: 15px;
}

/* Site header – transparent bar like main site (logo left, nav right, 120px) */
.materijal-preview__site-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  height: 120px;
  font-family: 'Open Sans', sans-serif;
}

.materijal-preview__ww {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
}

.materijal-preview__nav-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 120px;
}

.materijal-preview__logo {
  font-family: 'PT Serif', serif;
  font-size: 18px;
  font-weight: 400;
  color: #fff;
  letter-spacing: 0.02em;
}

.materijal-preview__nav {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: right;
}

.materijal-preview__nav li {
  display: inline-block;
  margin-left: 35px;
  padding: 0;
  line-height: 120px;
  vertical-align: top;
}

.materijal-preview__nav li:first-child {
  margin-left: 0;
}

.materijal-preview__nav li span {
  font-size: 15px;
  font-weight: 200;
  color: #fff;
  text-decoration: none;
}

.materijal-preview__nav-dd {
  display: inline-flex;
  align-items: center;
}

.materijal-preview__caret {
  display: inline-block;
  width: 12px;
  height: 6px;
  margin-left: 5px;
  background: transparent no-repeat center;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 6'%3E%3Cpath fill='%23fff' d='M0 0l6 6 6-6z'/%3E%3C/svg%3E");
  background-size: 12px 6px;
  vertical-align: middle;
}

/* Hero – matches main site Cover */
.materijal-preview__cover {
  width: 100%;
  height: 460px;
  background-position: center;
  background-size: cover;
  background-color: #e2e8f0;
  position: relative;
}

.materijal-preview__cover-ww {
  position: absolute;
  bottom: 130px;
  left: 0;
  right: 0;
  margin: 0 auto;
  max-width: 1180px;
  padding: 0 20px;
  box-sizing: border-box;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.materijal-preview__cover-title {
  font-family: 'PT Serif', serif;
  font-size: 48px;
  line-height: 64px;
  color: #fff;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.materijal-preview__cover-ww .materijal-preview__lang {
  font-size: 15px;
  font-weight: 200;
  color: #fff;
  font-family: 'Open Sans', sans-serif;
}

.materijal-preview__cover-border {
  position: absolute;
  bottom: 90px;
  left: 0;
  right: 0;
  border: 1px solid rgba(255, 255, 255, 0.3);
  z-index: 2;
}

.materijal-preview__cover-overlay {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: rgba(0, 0, 0, 0.3);
}

@media all and (max-width: 768px) {
  .materijal-preview__cover {
    .materijal-preview__cover-ww {
      bottom: 35px;
      display: block;
    }
    .materijal-preview__cover-title {
      font-size: 40px;
      margin-bottom: 90px;
    }
    .materijal-preview__cover-ww .materijal-preview__lang {
      position: absolute;
      bottom: 35px;
      right: 20px;
    }
  }
  .materijal-preview__nav-wrap .materijal-preview__nav {
    display: none;
  }
  .materijal-preview__footer {
    margin-top: 80px;
    padding: 50px 0 0;
  }
  .materijal-preview__footer-inner {
    padding: 50px 20px 40px;
  }
  .materijal-preview__footer-desc {
    float: none;
  }
  .materijal-preview__footer-desc p {
    width: 100%;
    max-width: 210px;
    font-size: 16px;
  }
  .materijal-preview__footer-right {
    float: none;
    margin-top: 40px;
    flex-wrap: wrap;
    justify-content: space-between;
  }
  .materijal-preview__footer-right li {
    margin-left: 0;
    margin-bottom: 40px;
  }
  .materijal-preview__footer-copy {
    margin-top: 50px;
    padding: 0 20px 40px;
  }
}

/* Main content – matches main site material page */
.materijal-preview__main {
  padding: 50px 20px 80px;
  box-sizing: border-box;
}

.materijal-preview__main.ww {
  max-width: 1180px;
  margin: 0 auto;
}

.materijal-preview__section {
  margin-bottom: 48px;
}

.materijal-preview__section:last-child {
  margin-bottom: 0;
}

.materijal-preview__section-title {
  font-size: 20px;
  margin: 0 0 20px 0;
  color: #000;
}

/* main-text matches main site .main-text */
.main-text {
  display: block;
  width: 100%;
  max-width: 100%;
  padding: 0;
  box-sizing: border-box;
  font-size: 22px;
  text-align: left;
  line-height: 32px;
  font-family: 'PT Serif', serif;
  font-weight: 400;
  color: #000;
}

.main-text :deep(p) {
  margin: 0 0 0.75rem 0;
}

.main-text :deep(p:last-child) {
  margin-bottom: 0;
}

.materijal-preview__video-wrap {
  position: relative;
  width: 100%;
  max-width: 800px;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
}

.materijal-preview__video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.materijal-preview__gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.materijal-preview__gallery-img {
  width: 100%;
  height: 240px;
  object-fit: cover;
  display: block;
}

/* Footer – matches main site MainFooter (layout, typography, border) */
.materijal-preview__footer {
  padding: 100px 0 75px;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  font-family: 'Open Sans', sans-serif;
  border-top: 1px solid #dcdcdc;
  margin-top: 150px;
  background: #e9edf2;
}

.materijal-preview__footer-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 60px 40px 50px;
  box-sizing: border-box;
  background: #fff;
  overflow: hidden;
}

.materijal-preview__footer-desc {
  float: left;
}

.materijal-preview__footer-desc h2 {
  font-family: 'PT Serif', serif;
  font-size: 24px;
  line-height: 32px;
  color: #767676;
  margin: 0;
}

.materijal-preview__footer-desc p {
  margin-top: 40px;
  width: 300px;
  font-size: 20px;
  color: #000;
  line-height: 27px;
  font-family: 'PT Serif', serif;
}

.materijal-preview__footer-right {
  float: right;
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
}

.materijal-preview__footer-right li {
  margin-left: 70px;
}

.materijal-preview__footer-right li:first-child {
  margin-left: 0;
}

.materijal-preview__footer-right span {
  display: block;
  margin-bottom: 40px;
  color: #7b7b7b;
  font-family: 'PT Serif', serif;
  font-size: 14px;
  line-height: 19px;
}

.materijal-preview__footer-right p {
  margin: 0;
  font-size: 14px;
  line-height: 19px;
  color: #000;
}

.materijal-preview__footer-products p {
  line-height: 26px;
}

.materijal-preview__footer-copy {
  clear: both;
  max-width: 1180px;
  margin: 100px auto 0;
  padding: 0 40px 40px;
  box-sizing: border-box;
  font-family: 'Oswald', sans-serif;
  font-size: 14px;
  line-height: 21px;
  color: #767676;
}

.materijal-preview__footer-copy a {
  color: #000;
  text-decoration: none;
}

.materijal-preview__footer-copy a:hover {
  color: #28c0d7;
}

.materijal-preview__empty,
.materijal-preview__loading {
  padding: 3rem;
  text-align: center;
  color: #94a3b8;
  margin: 0;
}
</style>
